import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { collection, collectionGroup, doc, getDoc, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useAuth } from './useAuth.js'

// State reducer for efficient state management and minimal re-renders
const userDevicesReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null }
    case 'SUCCESS':
      return { devices: action.payload, loading: false, error: null }
    case 'ERROR':
      return { devices: [], loading: false, error: action.payload }
    case 'RESET':
      return { devices: [], loading: false, error: null }
    default:
      return state
  }
}

/**
 * Firebase v11+ User Devices Hook
 * 
 * React 19+ optimized real-time user devices monitoring with minimal re-renders.
 * Implements userDevices/{userId}/devices/{serialNumber} subcollection pattern
 * with enhanced device information from deviceMetadata and deviceStatus collections.
 * 
 * Features:
 * - useReducer for optimal state management
 * - Memoized handlers for performance
 * - Server timestamp integration
 * - Real-time updates with efficient cleanup
 * - Error boundaries integration ready
 * 
 * @returns {Object} User devices state with loading/error states
 */
export function useUserDevices() {
  const [state, dispatch] = useReducer(userDevicesReducer, {
    devices: [],
    loading: false,
    error: null,
  })
  const { user } = useAuth()

  // Check if current user is a sysadmin
  const checkSysAdminStatus = useCallback(async (userId) => {
    if (!userId) return false
    try {
      const userDoc = await getDoc(doc(db, 'users', userId))
      const isSysAdmin = userDoc.exists() && userDoc.data().isSysAdmin === true
      return isSysAdmin
    } catch (error) {
      console.error('Error checking sysadmin status:', error)
      return false
    }
  }, [])

  // Memoized device data enrichment function
  const enrichDeviceData = useCallback(async (serialNumber, userDeviceData) => {
    const [metadataDoc, statusDoc] = await Promise.all([
      getDoc(doc(db, 'deviceMetadata', serialNumber)),
      getDoc(doc(db, 'deviceStatus', serialNumber)),
    ])

    const metadata = metadataDoc.exists() ? metadataDoc.data() : {}
    const status = statusDoc.exists() ? statusDoc.data() : {}

    return {
      deviceId: serialNumber,
      serialNumber,
      
      // User-device relationship info with server timestamps
      permissions: userDeviceData.permissions || 'user',
      addedAt: userDeviceData.addedAt || null,
      addedBy: userDeviceData.addedBy || '',
      lastAccessedAt: userDeviceData.lastAccessedAt || null,
      
      // Device metadata (shared across users)
      nickname: metadata.nickname || `Device ${serialNumber}`,
      location: metadata.location || '',
      createdBy: metadata.createdBy || '',
      createdAt: metadata.createdAt || null,
      lastModifiedBy: metadata.lastModifiedBy || '',
      lastModified: metadata.lastModified || null,
      
      // Device status information with preserved Firestore timestamps
      connectionStatus: status.connectionStatus || 'unknown',
      healthStatus: status.healthStatus || 'unknown',
      lastSeen: status.lastSeen || null,
      lastHeartbeat: status.lastHeartbeat || null,
      healthChecks: status.healthChecks || {
        network: false,
        sensors: false,
        storage: false,
        configuration: false,
      },
      performance: status.performance || {},
      alertsActive: status.alertsActive || 0,
      lastConfigSync: status.lastConfigSync || null,
      firmwareVersion: status.firmwareVersion || 'Unknown',
      deviceLocation: status.location || null,
      
      // Computed properties for UI convenience
      isOnline: status.connectionStatus === 'online',
      isHealthy: status.healthStatus === 'healthy',
      hasAlerts: (status.alertsActive || 0) > 0,
      canEdit: ['admin', 'technician'].includes(userDeviceData.permissions),
      canManage: userDeviceData.permissions === 'admin',
    }
  }, [])

  // Memoized device data update function
  const updateDeviceData = useCallback(async (deviceSerialNumbers, userDevicesMap) => {
    if (deviceSerialNumbers.size === 0) {
      dispatch({ type: 'SUCCESS', payload: [] })
      return
    }

    try {
      const devicePromises = Array.from(deviceSerialNumbers).map(async (serialNumber) => {
        const userDeviceData = userDevicesMap.get(serialNumber)
        if (!userDeviceData) return null
        return enrichDeviceData(serialNumber, userDeviceData)
      })

      const deviceList = (await Promise.all(devicePromises)).filter(Boolean)
      dispatch({ type: 'SUCCESS', payload: deviceList })
    } catch (error) {
      console.error('Device data update error:', error)
      dispatch({ type: 'ERROR', payload: error.message })
    }
  }, [enrichDeviceData])

  // Real-time subscription effect with optimized listeners
  useEffect(() => {
    if (!user?.uid) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({ type: 'LOADING' })

    let unsubscribeUserDevices = null
    const deviceUnsubscribers = new Map()
    let userDevicesMap = new Map()
    let currentSerialNumbers = new Set()
    let isSetupComplete = false

    // Function to get current device data without closure capture issues
    const getCurrentDeviceUpdate = () => {
      updateDeviceData(currentSerialNumbers, userDevicesMap)
    }

    // Setup individual device listeners for metadata and status
    const setupDeviceListeners = (serialNumbers) => {
      // Only setup if the main listener setup is complete
      if (!isSetupComplete) return

      // Update the current serial numbers reference
      currentSerialNumbers = new Set(serialNumbers)

      // Cleanup removed devices
      for (const [serialNumber, unsubscriber] of deviceUnsubscribers.entries()) {
        if (!serialNumbers.has(serialNumber)) {
          unsubscriber()
          deviceUnsubscribers.delete(serialNumber)
        }
      }

      // Add listeners for new devices
      serialNumbers.forEach(serialNumber => {
        if (!deviceUnsubscribers.has(serialNumber)) {
          const metadataUnsubscribe = onSnapshot(
            doc(db, 'deviceMetadata', serialNumber),
            () => getCurrentDeviceUpdate(),
            (error) => console.error(`Metadata listener error for ${serialNumber}:`, error),
          )

          const statusUnsubscribe = onSnapshot(
            doc(db, 'deviceStatus', serialNumber),
            () => getCurrentDeviceUpdate(),
            (error) => console.error(`Status listener error for ${serialNumber}:`, error),
          )

          deviceUnsubscribers.set(serialNumber, () => {
            metadataUnsubscribe()
            statusUnsubscribe()
          })
        }
      })
    }

    // Check if user is sysadmin and setup appropriate listener
    const setupDeviceListener = async () => {
      try {
        const isSysAdmin = await checkSysAdminStatus(user.uid)
        
        if (isSysAdmin) {
          // For sysadmins: Use collection group to get ALL devices across all users
          const allDevicesQuery = collectionGroup(db, 'devices')
          
          unsubscribeUserDevices = onSnapshot(
            allDevicesQuery,
            (snapshot) => {
              const newUserDevicesMap = new Map()
              const newSerialNumbers = new Set()

              snapshot.docs.forEach(doc => {
                const serialNumber = doc.id
                const data = doc.data()
                // Give sysadmins 'sysadmin' permission for all devices
                newUserDevicesMap.set(serialNumber, {
                  ...data,
                  permissions: 'sysadmin',
                  addedBy: 'system',
                  source: 'sysadmin-global-access',
                })
                newSerialNumbers.add(serialNumber)
              })

              userDevicesMap = newUserDevicesMap
              currentSerialNumbers = new Set(newSerialNumbers)
              isSetupComplete = true
              setupDeviceListeners(newSerialNumbers)
              updateDeviceData(newSerialNumbers, userDevicesMap)
            },
            (error) => {
              console.error('Sysadmin devices listener error:', error)
              dispatch({ type: 'ERROR', payload: error.message })
            },
          )
        } else {
          // For regular users: Use normal userDevices collection
          const userDevicesQuery = query(
            collection(db, `userDevices/${user.uid}/devices`),
            orderBy('addedAt', 'desc'),
          )

          unsubscribeUserDevices = onSnapshot(
            userDevicesQuery,
            (snapshot) => {
              const newUserDevicesMap = new Map()
              const newSerialNumbers = new Set()

              snapshot.docs.forEach(doc => {
                const serialNumber = doc.id
                newUserDevicesMap.set(serialNumber, doc.data())
                newSerialNumbers.add(serialNumber)
              })

              userDevicesMap = newUserDevicesMap
              currentSerialNumbers = new Set(newSerialNumbers)
              isSetupComplete = true
              setupDeviceListeners(newSerialNumbers)
              updateDeviceData(newSerialNumbers, userDevicesMap)
            },
            (error) => {
              console.error('User devices listener error:', error)
              dispatch({ type: 'ERROR', payload: error.message })
            },
          )
        }
      } catch (error) {
        console.error('Error setting up device listener:', error)
        dispatch({ type: 'ERROR', payload: error.message })
      }
    }

    setupDeviceListener()

    return () => {
      isSetupComplete = false
      unsubscribeUserDevices?.()
      deviceUnsubscribers.forEach(unsubscriber => unsubscriber())
      deviceUnsubscribers.clear()
    }
  }, [user?.uid, updateDeviceData, checkSysAdminStatus])

  // Return memoized state to prevent unnecessary re-renders
  return useMemo(() => state, [state])
}
